import React from 'react';
import { Trial } from '../../types';

interface SocReportFiltersProps {
  statusFilter: 'All' | Trial['preferredStatus'];
  onStatusFilterChange: (status: 'All' | Trial['preferredStatus']) => void;
  guidelineFilter: 'nccn' | 'non-nccn';
  onGuidelineFilterChange: (guideline: 'nccn' | 'non-nccn') => void;
  /** Hide guideline type toggle (e.g. NCCN regimens list is NCCN-only). */
  hideGuidelineFilter?: boolean;
  className?: string;
}

const GUIDELINE_OPTIONS: Array<{ id: 'nccn' | 'non-nccn'; label: string }> = [
  { id: 'nccn', label: 'NCCN' },
  { id: 'non-nccn', label: 'Non-NCCN' },
];

const SocReportFilters: React.FC<SocReportFiltersProps> = ({
  statusFilter,
  onStatusFilterChange,
  guidelineFilter,
  onGuidelineFilterChange,
  hideGuidelineFilter = false,
  className = '',
}) => (
  <div className={`flex items-center justify-between flex-wrap gap-4 ${className}`}>
    <div className="flex items-center gap-3">
      <label className="text-xs font-semibold uppercase tracking-wide text-slate-400" htmlFor="soc-status-filter">
        NCCN status
      </label>
      <select
        id="soc-status-filter"
        value={statusFilter}
        onChange={(event) => onStatusFilterChange(event.target.value as 'All' | Trial['preferredStatus'])}
        disabled={!hideGuidelineFilter && guidelineFilter === 'non-nccn'}
        className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-xs font-semibold text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 disabled:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
      >
        <option value="All">All Statuses</option>
        <option value="Preferred">Preferred</option>
        <option value="Other Recommended">Other Recommended</option>
        <option value="Useful in Certain Circumstances">Useful in Certain Circumstances</option>
      </select>
    </div>
    {!hideGuidelineFilter && (
      <div className="flex items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Guideline type</span>
        <div role="group" aria-label="Guideline type" className="bg-slate-100 p-1.5 rounded-xl flex flex-wrap gap-1">
          {GUIDELINE_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onGuidelineFilterChange(option.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                guidelineFilter === option.id
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
              }`}
              aria-pressed={guidelineFilter === option.id}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default SocReportFilters;
